import React, { useState } from "react";
import type { FC } from 'react';
import { toast } from "react-hot-toast";
import axios from "redaxios";
import { tw } from "twind";
import Checkbox from "@/components/lib/Checkbox/Checkbox";
import { Modal } from "./Modal";
import PhoneNumberInput from "./PhoneNumberInput";
import { Button } from "./Button";
import { countryDropdownStyle, phoneInputStyle } from "./ModalShipping";
import { useRudderstack } from "analytics/RudderStack";
import { useCartStore } from "store/cart";

interface DiscountModalProps {
  isOpen: boolean;
  requestClose: () => void;
  onSuccess: () => void;
  cartId: string;
  creatorId: string;
  message: string | null;
}

export const DiscountModal: FC<DiscountModalProps> = ({ isOpen, requestClose, onSuccess, cartId, creatorId, message }) => {
  const rudder = useRudderstack();

  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);
  const [isAgree, setIsAgree] = useState(false);

  const { cart, theme, countryCode, onDiscountApplied } = useCartStore();

  if (!cart) return null;

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!valid) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);

    const promise = new Promise<void>((resolve, reject) => {
      return axios
          .post('/api/subscribe', { creator_id: creatorId, phone_number: phone, cart_id: cartId })
          .then(function (response: any) {
            rudder.track('authentication', {
              cart_id: cartId,
              step: 2,
            });

            // 2 sec delay according to
            // https://creatorcartworkspace.slack.com/archives/C047ZBJT0UR/p1695836063899899?thread_ts=1695318106.269399&cid=C047ZBJT0UR
            setTimeout(() => {
              onDiscountApplied();
              resolve();
            }, cart?.popup_timeout);
          })
          .catch(function (err) {
            console.error(err);
            setLoading(false);
            reject();
          });
    }).then(() => {
      setLoading(false);
      onSuccess();
    });

    toast.promise(promise, {
      loading: 'Processing...',
      success: cart?.discount_applied_template ?? 'Phone number verified. Discounts applied',
      error: 'Something went wrong',
    });

    await promise;
  };

  return (
    <Modal open={isOpen} requestClose={requestClose}>
      <form onSubmit={onSubmit}>
        <div>
          <p className={tw`text-md text-${theme}-primary`}>{message}</p>
          <label htmlFor="phone" className={tw`block text-sm font-medium text-${theme}-primary mt-4`}>
            Phone Number:
          </label>
          <PhoneNumberInput
            showFlag
            value={phone}
            onChange={setPhone}
            isValid={setValid}
            styleConfig={phoneInputStyle}
            selectStyleConfig={countryDropdownStyle}
            defaultCountryCode={countryCode}
          />
        </div>
        <div className={tw`mt-5 sm:mt-6`}>
          <Checkbox id={'subscription-policies-checkbox'} isRequired value={isAgree} onChange={setIsAgree} label={<>I agree to receive promotional messages and to CreatorCart’s <a target='_blank' href="https://www.creatorcart.com/terms-and-conditions"
                                                                                                                                                                                    rel="noreferrer">Terms of Service</a>&nbsp;/&nbsp;<a target='_blank'
                                                                                                                                                                                                                                         href="https://www.creatorcart.com/privacy-policy"
                                                                                                                                                                                                                                         rel="noreferrer">Privacy Policy.</a></>} />
          <Button
            className={tw`mt-2 inline-flex items-center justify-center px-6 py-3 text-sm leading-4 font-bold bg-${theme} text-white relative disabled:cursor-not-allowed focus:outline-none focus:ring-offset-2 focus:ring-white-500 disabled:opacity-60 text-center w-full`}
            type="submit"
            loading={loading}
            disabled={!isAgree}
            showTooltip={!isAgree}
            tooltipBody={'To enable the button, please check the box above'}
            tooltipId={'subscription-button-tooltip'}
          >
            Subscribe
          </Button>
        </div>
      </form>
    </Modal>
  );
};
